import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import AuthLayout from "../../components/Auth/AuthLayout";
import {
  EyeSlashIcon,
  LoginEyeIcon,
  LoginIcon,
} from "../../components/Auth/AuthIcons";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  forgotPasswordEmailSchema,
  forgotPasswordOtpSchema,
  resetPasswordSchema,
} from "../../schemas/Auth.schema";
import {
  requestPasswordResetOtp,
  resetPassword,
  verifyPasswordResetOtp,
} from "../../services/forgotPasswordService";
import { extractApiErrorMessage, useToast } from "../../services/utils";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type ForgotStep = "email" | "otp" | "reset" | "success";

const RESEND_COOLDOWN_SECONDS = 30;

// Masks the local part of an email so the OTP screen can confirm the target
// address without echoing it back in full, e.g. jane.doe@velocity.com ->
// j******e@velocity.com
export const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");
  if (!domain) return email;
  if (localPart.length <= 2) {
    return `${localPart.charAt(0)}*@${domain}`;
  }
  return `${localPart.charAt(0)}${"*".repeat(
    localPart.length - 2,
  )}${localPart.charAt(localPart.length - 1)}@${domain}`;
};

const Forgot = () => {
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<ForgotStep>("email");
  const [email, setEmail] = useState("");
  // Reset tokens are kept in ephemeral component state only: never logged,
  // persisted or routed anywhere.
  const [requestToken, setRequestToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const otpInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step === "otp") {
      otpInputRef.current?.focus();
    } else if (step === "reset") {
      passwordInputRef.current?.focus();
    }
  }, [step]);

  const emailFormik = useFormik({
    initialValues: { email: "" },
    validationSchema: forgotPasswordEmailSchema,
    onSubmit: async (values) => {
      const trimmedEmail = values.email.trim();
      try {
        setIsLoading(true);
        const result = await requestPasswordResetOtp(trimmedEmail);
        if (!result?.token) return;
        setEmail(trimmedEmail);
        setRequestToken(result.token);
        setResetToken("");
        setResendIn(RESEND_COOLDOWN_SECONDS);
        setStep("otp");
        showToast(t("auth.forgotPassword.toast.otpSent"), "success");
      } catch (error) {
        showToast(
          extractApiErrorMessage(
            error,
            t("auth.forgotPassword.toast.otpSendFailed"),
          ),
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  const otpFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: forgotPasswordOtpSchema,
    onSubmit: async (values) => {
      if (!email || !requestToken) return;
      const trimmedOtp = values.otp.trim();
      try {
        setIsLoading(true);
        const result = await verifyPasswordResetOtp(requestToken, trimmedOtp);
        if (!result?.reset_token) return;
        setResetToken(result.reset_token);
        setStep("reset");
      } catch (error) {
        const message = extractApiErrorMessage(
          error,
          t("auth.forgotPassword.toast.otpInvalid"),
        );
        otpFormik.setFieldError("otp", message);
        showToast(message, "error");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const resetFormik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      if (!resetToken) return;
      try {
        setIsLoading(true);
        const result = await resetPassword(
          resetToken,
          values.password,
          values.confirmPassword,
        );
        if (!result) return;
        setRequestToken("");
        setResetToken("");
        setStep("success");
        showToast(t("auth.forgotPassword.toast.resetSuccess"), "success");
      } catch (error) {
        showToast(
          extractApiErrorMessage(
            error,
            t("auth.forgotPassword.toast.resetFailed"),
          ),
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleChangeEmail = useCallback(() => {
    otpFormik.resetForm();
    setRequestToken("");
    setResetToken("");
    setResendIn(0);
    setStep("email");
  }, [otpFormik]);

  const handleResendOtp = useCallback(async () => {
    if (!email || resendIn > 0) return;
    try {
      setIsLoading(true);
      const result = await requestPasswordResetOtp(email);
      if (!result?.token) return;
      setRequestToken(result.token);
      setResendIn(RESEND_COOLDOWN_SECONDS);
      showToast(t("auth.forgotPassword.toast.otpResent"), "success");
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("auth.forgotPassword.toast.otpSendFailed"),
        ),
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  }, [email, resendIn, showToast, t]);

  const renderEmailStep = () => (
    <>
      <Typography component="h1" className="auth-form-title">
        {t("auth.forgotPassword.email.header")}
      </Typography>
      <Typography className="auth-form-subtitle">
        {t("auth.forgotPassword.email.subtitle")}
      </Typography>

      <form onSubmit={emailFormik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputLabel className="label" htmlFor="email">
              {t("auth.forgotPassword.email.label")}
              <span className="required-asterisk">*</span>
            </InputLabel>
            <TextField
              autoComplete="email"
              name="email"
              type="email"
              variant="outlined"
              required
              fullWidth
              id="email"
              autoFocus
              placeholder={t("auth.forgotPassword.email.placeholder")}
              className="form-control"
              onChange={emailFormik.handleChange}
              onBlur={emailFormik.handleBlur}
              value={emailFormik.values.email}
              error={
                emailFormik.touched.email && Boolean(emailFormik.errors.email)
              }
              helperText={emailFormik.touched.email && emailFormik.errors.email}
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className="btn-orange btn-dark"
          disabled={isLoading}
        >
          {t("auth.forgotPassword.email.button")}
        </Button>

        <Grid item xs={12} className="regular-text">
          <Link to="/login">{t("auth.forgotPassword.backToSignIn")}</Link>
        </Grid>
      </form>
    </>
  );

  const renderOtpStep = () => (
    <>
      <Typography component="h1" className="auth-form-title">
        {t("auth.forgotPassword.otp.header")}
      </Typography>
      <Typography className="auth-form-subtitle">
        {t("auth.forgotPassword.otp.subtitle", { email: maskEmail(email) })}
      </Typography>

      <form onSubmit={otpFormik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputLabel className="label withLink" htmlFor="otp">
              <span>
                {t("auth.forgotPassword.otp.label")}
                <span className="required-asterisk">*</span>
              </span>
              <Button
                type="button"
                className="auth-link-button"
                onClick={handleResendOtp}
                disabled={isLoading || resendIn > 0}
              >
                {resendIn > 0
                  ? t("auth.forgotPassword.otp.resendCountdown", {
                      seconds: resendIn,
                    })
                  : t("auth.forgotPassword.otp.resend")}
              </Button>
            </InputLabel>
            <TextField
              name="otp"
              variant="outlined"
              required
              fullWidth
              id="otp"
              inputRef={otpInputRef}
              autoComplete="one-time-code"
              placeholder={t("auth.forgotPassword.otp.placeholder")}
              className="form-control"
              inputProps={{
                inputMode: "numeric",
                maxLength: 6,
              }}
              onChange={(event) =>
                otpFormik.setFieldValue(
                  "otp",
                  event.target.value.replace(/\D/g, "").slice(0, 6),
                )
              }
              onBlur={otpFormik.handleBlur}
              value={otpFormik.values.otp}
              error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
              helperText={otpFormik.touched.otp && otpFormik.errors.otp}
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className="btn-orange btn-dark"
          disabled={isLoading}
        >
          {t("auth.forgotPassword.otp.button")}
        </Button>

        <Grid item xs={12} className="regular-text">
          <Button
            type="button"
            className="auth-link-button"
            onClick={handleChangeEmail}
            disabled={isLoading}
          >
            {t("auth.forgotPassword.otp.changeEmail")}
          </Button>
        </Grid>
      </form>
    </>
  );

  const renderResetStep = () => (
    <>
      <Typography component="h1" className="auth-form-title">
        {t("auth.forgotPassword.reset.header")}
      </Typography>
      <Typography className="auth-form-subtitle">
        {t("auth.forgotPassword.reset.subtitle")}
      </Typography>

      <form onSubmit={resetFormik.handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <InputLabel className="label" htmlFor="password">
              {t("auth.forgotPassword.reset.passwordLabel")}
              <span className="required-asterisk">*</span>
            </InputLabel>
            <TextField
              required
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              id="password"
              inputRef={passwordInputRef}
              autoComplete="new-password"
              placeholder={t("auth.forgotPassword.reset.passwordPlaceholder")}
              className="form-control withAdornment password-box"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <LoginEyeIcon /> : <EyeSlashIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={resetFormik.handleChange}
              onBlur={resetFormik.handleBlur}
              value={resetFormik.values.password}
              error={
                resetFormik.touched.password &&
                Boolean(resetFormik.errors.password)
              }
              helperText={
                resetFormik.touched.password && resetFormik.errors.password
              }
            />
          </Grid>
          <Grid item xs={12}>
            <InputLabel className="label" htmlFor="confirmPassword">
              {t("auth.forgotPassword.reset.confirmPasswordLabel")}
              <span className="required-asterisk">*</span>
            </InputLabel>
            <TextField
              required
              fullWidth
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              autoComplete="new-password"
              placeholder={t(
                "auth.forgotPassword.reset.confirmPasswordPlaceholder",
              )}
              className="form-control withAdornment password-box"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <LoginEyeIcon />
                      ) : (
                        <EyeSlashIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={resetFormik.handleChange}
              onBlur={resetFormik.handleBlur}
              value={resetFormik.values.confirmPassword}
              error={
                resetFormik.touched.confirmPassword &&
                Boolean(resetFormik.errors.confirmPassword)
              }
              helperText={
                resetFormik.touched.confirmPassword &&
                resetFormik.errors.confirmPassword
              }
            />
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className="btn-orange btn-dark"
          disabled={isLoading}
        >
          {t("auth.forgotPassword.reset.button")}
        </Button>

        <Grid item xs={12} className="regular-text">
          <Link to="/login">{t("auth.forgotPassword.backToSignIn")}</Link>
        </Grid>
      </form>
    </>
  );

  const renderSuccessStep = () => (
    <Box role="status" aria-live="polite">
      <Box className="auth-success-icon">
        <CheckCircleOutlineIcon />
      </Box>
      <Typography component="h1" className="auth-form-title">
        {t("auth.forgotPassword.success.header")}
      </Typography>
      <Typography className="auth-form-subtitle">
        {t("auth.forgotPassword.success.subtitle")}
      </Typography>
      <Button
        type="button"
        fullWidth
        variant="contained"
        color="primary"
        className="btn-orange btn-dark"
        startIcon={<LoginIcon />}
        onClick={() => navigate("/login")}
      >
        {t("auth.forgotPassword.success.button")}
      </Button>
    </Box>
  );

  return (
    <AuthLayout>
      <Loader isLoading={isLoading} />
      {step === "email" && renderEmailStep()}
      {step === "otp" && renderOtpStep()}
      {step === "reset" && renderResetStep()}
      {step === "success" && renderSuccessStep()}
    </AuthLayout>
  );
};

export default Forgot;
